// lib/models.ts
import { DiffusionParams } from '#types/types';
import { getDefaultFlux3DraftParams, getDefaultFlux3Params } from '@/components/models/Flux3ControlPanel';
import { getDefaultFluxTwoProParams } from '@/components/models/FluxTwoProControlPanel';
import { getDefaultHailuoFastParams } from '@/components/models/HailuoFastControlPanel';
import { getDefaultKling25TurboParams } from '@/components/models/Kling25TurboControlPanel';
import { getDefaultLtx23FastParams } from '@/components/models/Ltx23FastControlPanel';
import { getDefaultLumaRayFlashParams } from '@/components/models/LumaRayFlashControlPanel';
import { getDefaultVeo31FastParams } from '@/components/models/Veo31FastControlPanel';
import { getDefaultLatestVideoParams, LATEST_VIDEO_MODEL_IDS } from '#lib/latest-video-models';
import { getDefaultWanFastT2VParams } from '@/components/models/WanFastT2VControlPanel';
import { getDefaultWanI2VParams } from '@/components/models/WanI2VControlPanel';
import { getDefaultGeminiOmniFlashParams } from '@/components/models/GeminiOmniFlashControlPanel';
import { getDefaultKling3Params, getDefaultKling3OmniParams } from '@/components/models/KlingControlPanel';
import { getDefaultMagnificImageUpscaleParams } from '@/components/models/MagnificImageUpscaleControlPanel';
import { getDefaultSeedance2Params } from '@/components/models/Seedance2ControlPanel';
import { getDefaultTopazVideoUpscaleParams } from '@/components/models/TopazVideoUpscaleControlPanel';
import { getDefaultMagnificVideoUpscaleParams } from '@/components/models/MagnificVideoUpscaleControlPanel';
import { getDefaultMireloSfxParams } from '@/components/models/MireloSfxControlPanel';
import { getDefaultTopazImageEnhanceParams } from '@/components/models/TopazImageEnhanceControlPanel';
import { getDefaultSeedVR2Params } from '@/components/models/SeedVR2ControlPanel';
import { getDefaultQwenImage21Params } from '@/components/models/QwenImage21ControlPanel';
import { getDefaultNanoBananaParams } from '@/components/models/NanoBananaControlPanel';
import { getDefaultNanoBananaProParams } from '@/components/models/NanoBananaProControlPanel';
import { getDefaultSeedream4_5Params } from '@/components/models/Seedream4_5ControlPanel';
import { getDefaultQwenImage2ProParams } from '@/components/models/QwenImage2ProControlPanel';
import { getDefaultQwenEditMultiangleParams } from '@/components/models/QwenEditMultiangleControlPanel';
import { getDefaultGrokImagineParams } from '@/components/models/GrokImagineControlPanel';
import { getDefaultGrokImagine2Params } from '@/components/models/GrokImagine2ControlPanel';
import { getDefaultMuseImageParams } from '@/components/models/MuseImageControlPanel';
import { getDefaultFlux3ImageParams } from '@/components/models/Flux3ImageControlPanel';
import { getDefaultQwenImage3Params } from '@/components/models/QwenImage3ControlPanel';
import { getDefaultNanoBanana2Params } from '@/components/models/NanoBanana2ControlPanel';
import { getDefaultGptImage2Params } from '@/components/models/GptImage2ControlPanel';
import { getDefaultGptImage25Params } from '@/components/models/GptImage25ControlPanel';
import { getDefaultMai25FlashParams } from '@/components/models/Mai25FlashControlPanel';
import { getDefaultIdeogramEditParams } from '@/components/models/IdeogramControlPanel';
import { getDefaultIdeogramInpaintParams } from '@/components/models/IdeogramInpaintControlPanel';
import { getDefaultIdeogramGenerateParams } from '@/components/models/IdeogramGenerateControlPanel';
import { getDefaultArrowVectorParams, getDefaultRecraftVectorParams } from '@/components/models/VectorControlPanel';

export const modelDefaultParamGetters: { [key: string]: () => DiffusionParams } = {
  'flux-2-pro': getDefaultFluxTwoProParams,
  'flux-2-klein-4b': getDefaultFluxTwoProParams,
  'fater-enhance': getDefaultFluxTwoProParams,
  'fater-upscale': getDefaultFluxTwoProParams,
  'topaz-image-enhance': getDefaultTopazImageEnhanceParams,
  'kling-3': getDefaultKling3Params,
  'kling-3-omni': getDefaultKling3OmniParams,
  'seedance-2.0': getDefaultSeedance2Params,
  'seedance-2.0-fast': getDefaultSeedance2Params,
  'seedance-2.5': getDefaultSeedance2Params,
  'flux-3': getDefaultFlux3Params,
  'flux-3-draft': getDefaultFlux3DraftParams,
  'gemini-omni-flash-preview': getDefaultGeminiOmniFlashParams,
  'hailuo-02-fast': getDefaultHailuoFastParams,
  'wan-2.2-i2v': getDefaultWanI2VParams,
  'wan-2.2-fast': getDefaultWanFastT2VParams,
  'veo-3.1-fast': getDefaultVeo31FastParams,
  ...Object.fromEntries(LATEST_VIDEO_MODEL_IDS.map((id) => [id, () => getDefaultLatestVideoParams(id)])),
  'kling-2.5-turbo': getDefaultKling25TurboParams,
  'ltx-2.3-fast': getDefaultLtx23FastParams,
  'luma-ray-2-flash': getDefaultLumaRayFlashParams,
  'topaz-video-upscale': getDefaultTopazVideoUpscaleParams,
  'mirelo-sfx-1.6': getDefaultMireloSfxParams,
  'magnific-image-upscale': getDefaultMagnificImageUpscaleParams,
  seedvr2: getDefaultSeedVR2Params,
  'qwen-image-2.1': getDefaultQwenImage21Params,
  'magnific-video-upscale': getDefaultMagnificVideoUpscaleParams,
  'nano-banana': getDefaultNanoBananaParams,
  'nano-banana-pro': getDefaultNanoBananaProParams,
  'nano-banana-2': getDefaultNanoBanana2Params,
  'nano-banana-2.1': getDefaultNanoBanana2Params,
  'seedream4.5': getDefaultSeedream4_5Params,
  'gpt-image-2': getDefaultGptImage2Params,
  'gpt-image-2.5': getDefaultGptImage25Params,
  'mai-2.5-flash': getDefaultMai25FlashParams,
  'mai-2.5-pro': getDefaultMai25FlashParams,
  'mai-2.6-flash': getDefaultMai25FlashParams,
  'mai-2.6': getDefaultMai25FlashParams,
  'qwen-image-2-pro': getDefaultQwenImage2ProParams,
  'qwen-image-3-pro': getDefaultQwenImage3Params,
  'qwen-edit-multiangle': getDefaultQwenEditMultiangleParams,
  'grok-imagine-image': getDefaultGrokImagineParams,
  'grok-imagine-2': getDefaultGrokImagine2Params,
  'muse-image': getDefaultMuseImageParams,
  'flux-3-image': getDefaultFlux3ImageParams,
  'ideogram-4-edit': getDefaultIdeogramEditParams,
  'ideogram-inpaint': getDefaultIdeogramInpaintParams,
  'ideogram-3': getDefaultIdeogramGenerateParams,
  'recraft-v4-1-vector': getDefaultRecraftVectorParams,
  'recraft-v4-1-vector-pro': getDefaultRecraftVectorParams,
  'arrow-2': getDefaultArrowVectorParams,
  'arrow-2-telos': getDefaultArrowVectorParams,
};

export const getDefaultParamsForModel = (model: string): (() => DiffusionParams) | undefined => {
  const getter = modelDefaultParamGetters[model];
  if (!getter) {
    console.error(`Unknown model: ${model} for default params.`);
  }
  return getter;
};
